import React, { useState, useEffect } from 'react';
import {
    auth,
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    createUserProfileDocument // Import the new function
} from '../../firebase';
import { getAuthService, detectThirdPartyCookieSupport, showCookieRequiredMessage } from '../../utils/enhancedAuth';

const DEFAULT_HERO_IMAGE = 'https://images.unsplash.com/photo-1542751371-adc38448a05e?w=1920&q=80';

function AuthModal({ onClose, onAuthSuccess, heroImageUrl }) {
    const [isRegistering, setIsRegistering] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [displayName, setDisplayName] = useState(''); // Add state for display name
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [cookieSupport, setCookieSupport] = useState(null);

    const bgImage = heroImageUrl || DEFAULT_HERO_IMAGE;

    // Check for third-party cookie support on component mount
    useEffect(() => {
        let isMounted = true;
        const checkCookieSupport = async () => {
            const support = await detectThirdPartyCookieSupport();
            if (isMounted) setCookieSupport(support);
        };
        checkCookieSupport();

        // Check for redirect result
        getAuthService().handleRedirectResult()
            .then(async (result) => {
                if (result) {
                    if (result.user) {
                        await createUserProfileDocument(result.user);
                    }
                    if (onAuthSuccess) onAuthSuccess();
                    onClose();
                }
            })
            .catch((error) => {
                console.error('Redirect result error:', error);
                setError('การเข้าสู่ระบบไม่สำเร็จ กรุณาลองอีกครั้ง');
            });

        return () => { isMounted = false; };
    }, [onAuthSuccess, onClose]);

    const handleEmailPasswordAuth = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        try {
            if (isRegistering) {
                if (!displayName) {
                    setError("กรุณาใส่ชื่อที่แสดง");
                    setLoading(false);
                    return;
                }
                const { user } = await createUserWithEmailAndPassword(auth, email, password);
                // Create user profile in Firestore
                await createUserProfileDocument(user, { displayName });
            } else {
                await signInWithEmailAndPassword(auth, email, password);
            }
            if (onAuthSuccess) onAuthSuccess();
            onClose();
        } catch (err) {
            let friendlyError = "เกิดข้อผิดพลาดบางอย่าง";
            switch (err.code) {
                case 'auth/user-not-found':
                case 'auth/wrong-password':
                    friendlyError = "อีเมลหรือรหัสผ่านไม่ถูกต้อง";
                    break;
                case 'auth/email-already-in-use':
                    friendlyError = "อีเมลนี้มีผู้ใช้งานในระบบแล้ว";
                    break;
                case 'auth/weak-password':
                    friendlyError = "รหัสผ่านต้องมีความยาวอย่างน้อย 6 ตัวอักษร";
                    break;
                case 'auth/invalid-email':
                    friendlyError = "รูปแบบอีเมลไม่ถูกต้อง";
                    break;
                default:
                    friendlyError = "การยืนยันตัวตนล้มเหลว โปรดลองอีกครั้ง";
            }
            setError(friendlyError);
        }
        setLoading(false);
    };

    const handleSocialLogin = async () => {
        setLoading(true);
        setError('');

        try {
            // Show warning if cookies are not supported
            if (cookieSupport === false) {
                const proceed = window.confirm(
                    `⚠️ การเข้าสู่ระบบด้วย Google อาจไม่ทำงานเนื่องจากการตั้งค่าเบราว์เซอร์\n\n${showCookieRequiredMessage()}\n\nต้องการลองต่อไปหรือไม่?`
                );
                if (!proceed) {
                    setLoading(false);
                    return;
                }
            }

            const result = await getAuthService().signInWithGoogle();

            // If result is null, it means redirect was used
            if (result === null) {
                // signInWithRedirect was called, page will redirect
                return;
            }

            // Create user profile in Firestore for social logins
            if (result?.user) {
                await createUserProfileDocument(result.user);
                if (onAuthSuccess) onAuthSuccess();
                onClose();
            }
        } catch (err) {
            console.error('Social login error:', err);

            let friendlyError = "การเข้าสู่ระบบผ่าน Google ล้มเหลว";

            switch (err.code) {
                case 'auth/unauthorized-domain':
                    friendlyError = "โดเมนนี้ไม่ได้รับอนุญาตให้ใช้ Google Sign-in กรุณาติดต่อผู้ดูแลระบบ";
                    break;
                case 'auth/popup-blocked':
                    friendlyError = "เบราว์เซอร์ปิดกั้น popup กรุณาอนุญาต popup หรือลองอีกครั้ง";
                    break;
                case 'auth/cancelled-popup-request':
                case 'auth/popup-closed-by-user':
                    friendlyError = "การเข้าสู่ระบบถูกยกเลิก";
                    break;
                default:
                    if (cookieSupport === false) {
                        friendlyError = `เบราว์เซอร์บล็อก third-party cookies\n\n${showCookieRequiredMessage()}`;
                    }
            }

            setError(friendlyError);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-md z-50 flex justify-center items-center" onClick={onClose}>
            <div className="bg-white w-full max-w-md m-4 rounded-2xl shadow-2xl border border-gray-100 overflow-hidden" onClick={e => e.stopPropagation()}>
                {/* Header with hero image */}
                <div className="relative overflow-hidden" style={{ height: '160px' }}>
                    <img
                        src={bgImage}
                        alt=""
                        className="absolute inset-0 w-full h-full object-cover"
                        style={{ objectPosition: 'center 20%' }}
                    />
                    {/* Close button */}
                    <button onClick={onClose} className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center text-white/80 hover:text-white bg-black/30 hover:bg-black/50 backdrop-blur-sm transition-all">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                </div>

                {/* Content */}
                <div className="p-8">
                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-600 p-3 rounded-xl mb-6 text-center text-sm">
                            <span className="mr-1">⚠️</span> {error}
                        </div>
                    )}

                    <form onSubmit={handleEmailPasswordAuth} className="space-y-5">
                        {isRegistering && (
                            <div>
                                <label className="block text-gray-700 mb-1.5 font-medium text-sm" htmlFor="displayName">
                                    ชื่อที่แสดง
                                </label>
                                <input
                                    type="text"
                                    id="displayName"
                                    value={displayName}
                                    onChange={(e) => setDisplayName(e.target.value)}
                                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-all"
                                    placeholder="ชื่อที่จะแสดงให้คนอื่นเห็น"
                                    required
                                />
                            </div>
                        )}
                        <div>
                            <label className="block text-gray-700 mb-1.5 font-medium text-sm" htmlFor="email">
                                อีเมล
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                                </div>
                                <input
                                    type="email"
                                    id="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="w-full bg-gray-50 border border-gray-200 rounded-xl pl-12 pr-4 py-3 text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-all"
                                    placeholder="กรอกอีเมลของคุณ"
                                    required
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-gray-700 mb-1.5 font-medium text-sm" htmlFor="password">
                                รหัสผ่าน
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                                </div>
                                <input
                                    type="password"
                                    id="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full bg-gray-50 border border-gray-200 rounded-xl pl-12 pr-4 py-3 text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-all"
                                    placeholder="กรอกรหัสผ่าน"
                                    required
                                />
                            </div>
                        </div>
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full py-3 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold rounded-xl shadow-lg shadow-blue-500/25 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-[1.01] active:scale-[0.99]"
                        >
                            {loading ? (
                                <div className="flex items-center justify-center">
                                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                                    กำลังดำเนินการ...
                                </div>
                            ) : (
                                isRegistering ? 'สมัครสมาชิก' : 'เข้าสู่ระบบ'
                            )}
                        </button>
                    </form>

                    <div className="my-6 flex items-center">
                        <div className="flex-grow border-t border-gray-200"></div>
                        <span className="flex-shrink mx-4 text-gray-400 text-sm font-medium">หรือ</span>
                        <div className="flex-grow border-t border-gray-200"></div>
                    </div>

                    <div className="space-y-3">
                        {cookieSupport === false && (
                            <div className="bg-amber-50 border border-amber-200 text-amber-700 p-3 rounded-xl text-sm">
                                ⚠️ เบราว์เซอร์บล็อก third-party cookies การเข้าสู่ระบบด้วย Google อาจไม่ทำงาน
                            </div>
                        )}

                        <button
                            onClick={handleSocialLogin}
                            disabled={loading}
                            className="w-full py-3 bg-white border-2 border-gray-200 hover:border-gray-300 hover:bg-gray-50 text-gray-700 font-medium rounded-xl flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                        >
                            <svg className="w-5 h-5 mr-3" viewBox="0 0 48 48">
                                <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24s8.955,20,20,20s20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"></path>
                                <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"></path>
                                <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"></path>
                                <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571l6.19,5.238C42.012,36.494,44,30.836,44,24C44,22.659,43.862,21.35,43.611,20.083z"></path>
                            </svg>
                            เข้าสู่ระบบด้วย Google
                        </button>
                    </div>

                    <p className="mt-6 text-center text-gray-500 text-sm">
                        {isRegistering ? 'มีบัญชีอยู่แล้ว?' : 'ยังไม่มีบัญชี?'}
                        <button
                            onClick={() => setIsRegistering(!isRegistering)}
                            className="font-semibold text-blue-500 hover:text-blue-600 ml-2 transition-colors duration-200"
                        >
                            {isRegistering ? 'เข้าสู่ระบบที่นี่' : 'สมัครสมาชิกที่นี่'}
                        </button>
                    </p>
                </div>
            </div>
        </div>
    );
}

export default AuthModal;
